import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { getCompanyProfileByUserId } from '../services/companyService';
import { getSeekerProfile } from '../services/seekerProfileService';
import { deleteUser } from '../services/userService';
import LoadingSpinner from '../components/LoadingSpinner';

const ManageUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [enrichedUsers, setEnrichedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState({});
  const [deletingUser, setDeletingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const usersPerPage = 20;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const usersData = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const userData = { id: doc.id, ...doc.data() };

            // Auto-fetch seeker profile for job seekers
            if (userData.userType === 'jobseeker') {
              try {
                const result = await getSeekerProfile(userData.id);
                if (result.success) {
                  userData.seekerProfile = result.profile;
                }
              } catch (err) {
                console.error(`Error fetching seeker profile for ${userData.id}:`, err);
              }
            }

            return userData;
          })
        );
        setUsers(usersData);
        setEnrichedUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = enrichedUsers.filter(user => {
    let matchesFilter = false;

    if (filter === 'all') {
      matchesFilter = true;
    } else if (filter === 'seekerprofiles') {
      // Show only job seekers with complete profile data
      matchesFilter = user.userType === 'jobseeker' &&
        user.seekerProfile &&
        (
          (user.seekerProfile.education && user.seekerProfile.education.length > 0) ||
          (user.seekerProfile.experience && user.seekerProfile.experience.length > 0) ||
          (user.seekerProfile.skills && user.seekerProfile.skills.length > 0)
        );
    } else {
      matchesFilter = user.userType === filter;
    }

    const matchesSearch = searchQuery === '' ||
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.phone && user.phone.includes(searchQuery));

    return matchesFilter && matchesSearch;
  });

  // Reset to first page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const exportToCSV = async () => {
    if (filter === 'all') return;

    setIsExporting(true);
    try {
      // Fetch details for all users in the filtered list if missing
      const usersToExport = await Promise.all(filteredUsers.map(async (user) => {
        // Clone user to avoid mutating state directly
        const userWithDetails = { ...user };

        // Check if we need to fetch details
        const hasDetails = (user.userType === 'employer' && user.company) ||
          (user.userType === 'jobseeker' && user.seekerProfile);

        if (!hasDetails) {
          try {
            if (user.userType === 'employer') {
              const result = await getCompanyProfileByUserId(user.id);
              if (result.success) userWithDetails.company = result.data;
            } else if (user.userType === 'jobseeker') {
              const result = await getSeekerProfile(user.id);
              if (result.success) userWithDetails.seekerProfile = result.profile;
            }
          } catch (err) {
            console.error(`Error fetching details for user ${user.id}`, err);
          }
        }
        return userWithDetails;
      }));

      let headers = [];
      let csvData = [];

      const escapeCsvField = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        // Escape quotes and wrap in quotes if contains comma, quote or newline
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      if (filter === 'jobseeker') {
        headers = [
          'User ID', 'Full Name', 'Email', 'Phone', 'Joined Date',
          'Location', 'Skills', 'Experience Level', 'Expected Salary', 'Notice Period',
          'Current Position', 'Education', 'Experience History', 'Resume Available', 'LinkedIn Profile'
        ];

        csvData = usersToExport.map(user => {
          // Format Education
          const education = user.seekerProfile?.education?.map(edu => {
            const parts = [edu.degree, edu.institution, edu.year].filter(Boolean);
            return parts.join(' - ');
          }).join('; ') || '';

          // Format Experience
          const experience = user.seekerProfile?.experience?.map(exp => {
            const duration = `${exp.startDate || ''} to ${exp.isCurrentJob ? 'Present' : (exp.endDate || '')}`;
            return `${exp.jobTitle || 'Role'} at ${exp.company || 'Company'} (${duration})`;
          }).join('; ') || '';

          // Format Salary
          const salary = user.seekerProfile?.preferences?.expectedSalary
            ? `${user.seekerProfile.preferences.expectedSalary.min}-${user.seekerProfile.preferences.expectedSalary.max} ${user.seekerProfile.preferences.expectedSalary.currency}`
            : '';

          return [
            user.id,
            `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            user.email || '',
            user.phone || '',
            formatDate(user.createdAt),
            user.seekerProfile?.personalInfo?.location || '',
            user.seekerProfile?.skills?.join(', ') || '',
            user.seekerProfile?.preferences?.careerLevel || '',
            salary,
            user.seekerProfile?.preferences?.noticePeriod || '',
            user.seekerProfile?.experience?.[0]?.jobTitle || '',
            education,
            experience,
            user.seekerProfile?.resumeURL ? 'Yes' : 'No',
            user.seekerProfile?.personalInfo?.linkedIn || ''
          ].map(escapeCsvField);
        });
      } else if (filter === 'employer') {
        headers = [
          'User ID', 'Contact Person', 'Email', 'Phone', 'Joined Date',
          'Company Name', 'Company Industry', 'Company Size', 'Company Location', 'Company Type', 'Company Website'
        ];

        csvData = usersToExport.map(user => {
          return [
            user.id,
            `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            user.email || '',
            user.phone || '',
            formatDate(user.createdAt),
            user.company?.companyName || '',
            user.company?.industry || '',
            user.company?.companySize || '',
            user.company?.location || '',
            user.company?.companyType || '',
            user.company?.website || ''
          ].map(escapeCsvField);
        });
      } else {
        // Fallback for other types (agent, team) - minimal data
        headers = ['User ID', 'Full Name', 'Email', 'Phone', 'User Type', 'Joined Date'];
        csvData = usersToExport.map(user => [
          user.id,
          `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          user.email || '',
          user.phone || '',
          user.userType || '',
          formatDate(user.createdAt)
        ].map(escapeCsvField));
      }

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filter}_users_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleUserExpansion = async (userId) => {
    if (expandedUser === userId) {
      // Collapse if already expanded
      setExpandedUser(null);
      return;
    }

    // Expand and load detailed data if not already loaded
    setExpandedUser(userId);

    const user = enrichedUsers.find(u => u.id === userId);
    if (!user) return;

    // Check if detailed data is already loaded
    const hasDetailedData = (user.userType === 'employer' && user.company) ||
      (user.userType === 'jobseeker' && user.seekerProfile);

    if (!hasDetailedData) {
      setLoadingDetails(prev => ({ ...prev, [userId]: true }));

      try {
        let additionalData = {};

        if (user.userType === 'employer') {
          const companyResult = await getCompanyProfileByUserId(userId);
          if (companyResult.success) {
            additionalData.company = companyResult.data;
          }
        } else if (user.userType === 'jobseeker') {
          const seekerResult = await getSeekerProfile(userId);
          if (seekerResult.success) {
            additionalData.seekerProfile = seekerResult.profile;
          }
        }

        // Update the enriched users with the new data
        setEnrichedUsers(prev =>
          prev.map(u => u.id === userId ? { ...u, ...additionalData } : u)
        );
      } catch (error) {
        console.error('Error loading user details:', error);
      } finally {
        setLoadingDetails(prev => ({ ...prev, [userId]: false }));
      }
    }
  };

  // Calculate pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Auto-load details for all visible users (with glitch prevention)
  const processedUsers = React.useRef(new Set());

  useEffect(() => {
    currentUsers.forEach(user => {
      // Skip if already processed or currently loading
      if (processedUsers.current.has(user.id) || loadingDetails[user.id]) {
        return;
      }

      const hasDetailedData = (user.userType === 'employer' && user.company) ||
        (user.userType === 'jobseeker' && user.seekerProfile);

      if (!hasDetailedData) {
        processedUsers.current.add(user.id);
        toggleUserExpansion(user.id);
      }
    });
  }, [currentUsers, loadingDetails, toggleUserExpansion]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardClick = (user) => {
    // Navigate to admin user profile page
    navigate(`/admin/user/${user.id}`);
  };

  const handleDeleteUser = async (userId, userName, event) => {
    // Stop propagation to prevent card click
    event.stopPropagation();

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${userName}?\n\nThis will permanently delete:\n- User account from database\n- User authentication\n- All associated data\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingUser(userId);

    try {
      const result = await deleteUser(userId);

      if (result.success) {
        // Remove user from local state
        setUsers(prev => prev.filter(u => u.id !== userId));
        setEnrichedUsers(prev => prev.filter(u => u.id !== userId));

        // Close expanded view if this user was expanded
        if (expandedUser === userId) {
          setExpandedUser(null);
        }

        alert('User deleted successfully!');
      } else {
        alert(`Failed to delete user: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('An error occurred while deleting the user. Please try again.');
    } finally {
      setDeletingUser(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-950 rounded-3xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden border border-slate-800">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:30px_30px]" />
        <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-2 bg-gradient-to-r from-white via-slate-100 to-slate-300 bg-clip-text text-transparent">Identity & Platform Directory</h1>
            <p className="text-slate-400 font-medium max-w-xl text-sm sm:text-base">Audit, verify, and govern all active candidate profiles, enterprise credentials, and operational roles.</p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={isExporting || filter === 'all'}
            className={`bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-5 py-3 rounded-2xl flex items-center gap-2.5 transition-all duration-300 shadow-md shadow-emerald-950/10 hover:shadow-lg hover:shadow-emerald-950/20 transform hover:-translate-y-0.5 ${(isExporting || filter === 'all') ? 'opacity-50 cursor-not-allowed transform-none shadow-none' : ''
              }`}
            title={filter === 'all' ? "Please select a specific directory category to export data" : "Export CSV Directory"}
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Compiling...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV Directory
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters and Search Panel */}
      <div className="mb-8 bg-white/70 backdrop-blur-md rounded-2xl shadow-sm border border-slate-200/80 p-5">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5">
          <div className="flex space-x-2 overflow-x-auto pb-3 xl:pb-0 scrollbar-none">
            {['all', 'jobseeker', 'seekerprofiles', 'employer', 'agent', 'team'].map((type) => {
              const labelMap = {
                all: 'All Identities',
                jobseeker: 'Talent Pool',
                seekerprofiles: 'Complete Portfolios',
                employer: 'Enterprise',
                agent: 'Affiliate Partners',
                team: 'Operations Staff'
              };
              return (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap ${filter === type
                    ? 'bg-slate-900 text-white shadow-sm shadow-slate-900/10'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                    }`}
                >
                  {labelMap[type]} ({
                    type === 'all'
                      ? enrichedUsers.length
                      : type === 'seekerprofiles'
                        ? enrichedUsers.filter(u =>
                          u.userType === 'jobseeker' &&
                          u.seekerProfile &&
                          (
                            (u.seekerProfile.education && u.seekerProfile.education.length > 0) ||
                            (u.seekerProfile.experience && u.seekerProfile.experience.length > 0) ||
                            (u.seekerProfile.skills && u.seekerProfile.skills.length > 0)
                          )
                        ).length
                        : enrichedUsers.filter(u => u.userType === type).length
                  })
                </button>
              );
            })}
          </div>

          <div className="relative w-full xl:w-80">
            <input
              type="text"
              placeholder="Search by name, email, or contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900 focus:border-slate-900 bg-white/50 text-sm transition-all duration-200"
            />
            <svg className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="space-y-6">
        {currentUsers.map((user) => (
          <div
            key={user.id}
            className="bg-white rounded-3xl border border-slate-200/60 shadow-sm hover:shadow-xl hover:border-slate-350 transition-all duration-300 overflow-hidden cursor-pointer transform hover:-translate-y-0.5"
            onClick={() => handleCardClick(user)}
          >
            {/* Job Seeker Card - Enhanced attractive design */}
            {user.userType === 'jobseeker' && user.seekerProfile ? (
              <div className="p-6 bg-gradient-to-br from-slate-50/50 via-white to-indigo-50/10">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                  <div className="flex flex-col sm:flex-row items-start gap-4 flex-1">
                    {/* Avatar with gradient */}
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-slate-900 rounded-2xl flex items-center justify-center text-white font-bold text-2xl flex-shrink-0 shadow-md">
                      {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Name & Badge */}
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <h3 className="text-xl font-bold text-slate-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
                          Candidate
                        </span>
                      </div>

                      {/* Quick Stats - Experience, Salary, Location */}
                      <div className="flex items-center gap-2 text-xs mb-4 flex-wrap">
                        {user.seekerProfile.experience && user.seekerProfile.experience.length > 0 && user.seekerProfile.experience[0]?.yearsOfExperience && (
                          <div className="flex items-center gap-1.5 bg-slate-50 text-slate-700 px-3 py-1.5 rounded-xl border border-slate-100 font-medium">
                            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                            </svg>
                            <span>{user.seekerProfile.experience[0].yearsOfExperience} Exp</span>
                          </div>
                        )}
                        {user.seekerProfile.preferences?.expectedSalary && (
                          <div className="flex items-center gap-1.5 bg-slate-50 text-slate-700 px-3 py-1.5 rounded-xl border border-slate-100 font-medium">
                            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                            <span>{(user.seekerProfile.preferences.expectedSalary.min / 100000).toFixed(1)} Lakh</span>
                          </div>
                        )}
                        {user.seekerProfile.personalInfo?.location && (
                          <div className="flex items-center gap-1.5 bg-slate-50 text-slate-700 px-3 py-1.5 rounded-xl border border-slate-100 font-medium">
                            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{user.seekerProfile.personalInfo.location}</span>
                          </div>
                        )}
                      </div>

                      {/* Current/Latest Position */}
                      {user.seekerProfile.experience && user.seekerProfile.experience.length > 0 && (
                        <div className="mb-3 bg-white rounded-2xl p-3 shadow-sm border border-slate-100/80">
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Current Role</p>
                              <p className="text-sm text-slate-800">
                                <span className="font-semibold">{user.seekerProfile.experience[0].jobTitle}</span>
                                <span className="text-slate-400"> at </span>
                                <span className="font-semibold text-blue-650">{user.seekerProfile.experience[0].company}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Education */}
                      {user.seekerProfile.education && user.seekerProfile.education.length > 0 && (
                        <div className="mb-3 bg-white rounded-2xl p-3 shadow-sm border border-slate-100/80">
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Education</p>
                              <p className="text-sm text-slate-850">
                                {user.seekerProfile.education[0].degree}, <span className="font-semibold text-indigo-600">{user.seekerProfile.education[0].fieldOfStudy}</span>, {user.seekerProfile.education[0].institution}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Skills & Languages */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        {/* Skills */}
                        {user.seekerProfile.skills && user.seekerProfile.skills.length > 0 && (
                          <div className="bg-gradient-to-br from-indigo-50/30 to-slate-50/30 rounded-2xl p-3 border border-indigo-100/50">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Skills</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {user.seekerProfile.skills.slice(0, 4).map((skill, idx) => (
                                <span key={idx} className="bg-white text-slate-700 px-2 py-0.5 rounded-md text-xs font-semibold border border-slate-200/60 shadow-sm">
                                  {skill}
                                </span>
                              ))}
                              {user.seekerProfile.skills.length > 4 && (
                                <span className="text-xs text-slate-500 font-semibold">+{user.seekerProfile.skills.length - 4}</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Languages */}
                        {user.seekerProfile.languages && user.seekerProfile.languages.length > 0 && (
                          <div className="bg-gradient-to-br from-emerald-50/30 to-slate-50/30 rounded-2xl p-3 border border-emerald-100/50">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Languages</p>
                            </div>
                            <p className="text-xs text-slate-700 font-semibold">
                              {user.seekerProfile.languages.map((lang, idx) => (
                                <span key={idx} className="inline-block mr-2.5">
                                  {lang.language} <span className="text-emerald-700 font-bold">({lang.proficiency})</span>
                                </span>
                              ))}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Contact Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 mt-4 border-t border-slate-150">
                        <div className="bg-slate-50/80 rounded-xl p-2.5 border border-slate-100">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Email Address</p>
                          <p className="text-xs text-slate-800 font-semibold truncate">{user.email}</p>
                        </div>
                        <div className="bg-slate-50/80 rounded-xl p-2.5 border border-slate-100">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Phone Contact</p>
                          <p className="text-xs text-slate-800 font-semibold">{user.phone || user.seekerProfile.personalInfo?.phone || 'N/A'}</p>
                        </div>
                        <div className="bg-slate-50/80 rounded-xl p-2.5 border border-slate-100">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Account ID</p>
                          <p className="text-xs text-slate-500 font-mono truncate animate-pulse" title={user.id}>{user.id.substring(0, 10)}...</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delete Action */}
                  <button
                    onClick={(e) => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`, e)}
                    disabled={deletingUser === user.id}
                    className={`px-4 py-2.5 bg-white border border-red-200 hover:border-red-300 hover:bg-red-50 text-red-600 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 self-end lg:self-center shadow-sm hover:shadow ${deletingUser === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Delete user"
                  >
                    {deletingUser === user.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        <span className="text-sm">Deleting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span className="text-sm">Delete Account</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Enhanced Card Format for Employers, Agents, Team */
              <div className="p-6 bg-gradient-to-br from-slate-50/50 via-white to-emerald-50/10">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                  <div className="flex flex-col sm:flex-row items-start gap-4 flex-1">
                    {/* Avatar with gradient */}
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-slate-900 rounded-2xl flex items-center justify-center text-white font-bold text-2xl flex-shrink-0 shadow-md">
                      {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Name and User Type Badge */}
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <h3 className="text-xl font-bold text-slate-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${user.userType === 'employer' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                          user.userType === 'agent' ? 'bg-violet-50 text-violet-700 border-violet-100' :
                            user.userType === 'team' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                              'bg-slate-50 text-slate-700 border-slate-100'
                          }`}>
                          {user.userType === 'jobseeker' ? 'Candidate' : user.userType?.charAt(0).toUpperCase() + user.userType?.slice(1)}
                        </span>
                      </div>

                      {/* Joined Date */}
                      <p className="text-xs text-slate-450 mb-4 flex items-center gap-1">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                        </svg>
                        Joined: {formatDate(user.createdAt)}
                      </p>

                      {/* Company Details for Employers */}
                      {user.userType === 'employer' && user.company && (
                        <>
                          <div className="mb-3 bg-white rounded-2xl p-3 shadow-sm border border-slate-100/80">
                            <div className="flex items-start gap-2">
                              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-slate-550" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Corporate Entity</p>
                                <p className="text-sm font-bold text-slate-850">{user.company.companyName}</p>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Industry</p>
                              <p className="text-sm text-slate-850 font-semibold">{user.company.industry}</p>
                            </div>
                            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Company Size</p>
                              <p className="text-sm text-slate-850 font-semibold">{user.company.companySize}</p>
                            </div>
                            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Location</p>
                              <p className="text-sm text-slate-850 font-semibold">{user.company.location}</p>
                            </div>
                            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Founded</p>
                              <p className="text-sm text-slate-850 font-semibold">{user.company.foundedYear}</p>
                            </div>
                          </div>

                          {user.company.description && (
                            <div className="mb-3 bg-gradient-to-br from-slate-50/50 to-gray-50/50 rounded-2xl p-3 border border-slate-150">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Entity Summary</p>
                              <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">{user.company.description}</p>
                            </div>
                          )}
                        </>
                      )}

                      {/* Contact Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 mt-4 border-t border-slate-150">
                        <div className="bg-slate-50/80 rounded-xl p-2.5 border border-slate-100">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Email Address</p>
                          <p className="text-xs text-slate-800 font-semibold truncate">{user.email}</p>
                        </div>
                        <div className="bg-slate-50/80 rounded-xl p-2.5 border border-slate-100">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Phone Contact</p>
                          <p className="text-xs text-slate-800 font-semibold">{user.phone || 'N/A'}</p>
                        </div>
                        <div className="bg-slate-50/80 rounded-xl p-2.5 border border-slate-100">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Account ID</p>
                          <p className="text-xs text-slate-550 font-mono truncate" title={user.id}>{user.id.substring(0, 10)}...</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delete Action */}
                  <button
                    onClick={(e) => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`, e)}
                    disabled={deletingUser === user.id}
                    className={`px-4 py-2.5 bg-white border border-red-200 hover:border-red-300 hover:bg-red-50 text-red-600 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 self-end lg:self-center shadow-sm hover:shadow ${deletingUser === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Delete user"
                  >
                    {deletingUser === user.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        <span className="text-sm">Deleting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span className="text-sm">Delete Account</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {
        totalPages > 1 && filteredUsers.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-semibold">{indexOfFirstUser + 1}</span> to{' '}
                <span className="font-semibold">{Math.min(indexOfLastUser, filteredUsers.length)}</span> of{' '}
                <span className="font-semibold">{filteredUsers.length}</span> users
              </div>

              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors ${currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return (
                        <span key={pageNumber} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-500">No users match the selected filter.</p>
          </div>
        )
      }
    </div >
  );
};

export default ManageUsers;
